export const state = {
  geocodingHandler: defaultGeocodingHandler,
  mapboxAccessToken: '',
  reverseGeocodeEnabled: false,
};

/**
 * Cached reverse lookups for geocoded addresses.
 */
export const reverseLookups = {} as {
  [lonLat: string]: Promise<GeocodedAddress>;
};

export async function defaultGeocodingHandler(
  longitude: number,
  latitude: number
) {
  const key = `${longitude},${latitude}`;

  if (!(key in reverseLookups)) {
    reverseLookups[key] = new Promise((resolve, reject) => {
      void fetch(
        `https://api.mapbox.com/search/geocode/v6/reverse?longitude=${longitude}&latitude=${latitude}&types=address&access_token=${state.mapboxAccessToken}`
      )
        .then((response) => {
          response
            .json()
            .then((data: MapboxGeocodingResponse) => {
              if (data.features.length === 0) {
                reject(new Error('Address not found'));
                delete reverseLookups[key];
                return;
              }

              const addressProps = data.features[0]?.properties || {};

              const reverseLookup = {
                street: addressProps.name_preferred || addressProps.name || '',
                city: addressProps.context?.place?.name || '',
                state: addressProps.context?.region?.name || '',
              };

              resolve(reverseLookup);
            })
            .catch(reject);
        })
        .catch(reject);
    });
  }

  return reverseLookups[key];
}

export function isReverseGeocodingEnabled() {
  return state.reverseGeocodeEnabled;
}

export async function reverseGeocode(longitude: number, latitude: number) {
  return state.geocodingHandler(longitude, latitude);
}

export function setGeocodingHandler(
  handler: (longitude: number, latitude: number) => Promise<GeocodedAddress>
) {
  state.geocodingHandler = handler;
}

export function setMapboxAccessToken(token: string) {
  state.mapboxAccessToken = token;
}

export function setReverseGeocodingEnabled(enable: boolean) {
  state.reverseGeocodeEnabled = enable;
}

export type GeocodedAddress = {
  street: string;
  city: string;
  state: string;
};

export type MapboxGeocodingResponse = {
  type: 'FeatureCollection';
  features: MapboxGeocodingResponseFeature[];
  attribution: string;
};

export type MapboxGeocodingResponseFeature = {
  type: 'Feature';
  id: string;
  geometry: {
    type: 'Point';
    coordinates: [number, number];
  };
  properties: {
    mapbox_id: string;
    feature_type: 'address';
    full_address: string;
    name: string;
    name_preferred: string;
    coordinates: {
      longitude: number;
      latitude: number;
      accuracy:
        | 'rooftop'
        | 'parcel'
        | 'point'
        | 'interpolated'
        | 'approximate'
        | 'intersection';
      routable_points: {
        name: string;
        latitude: number;
        longitude: number;
      }[];
    };
    place_formatted: string;
    context: {
      address: {
        mapbox_id: string;
        address_number: string;
        street_name: string;
        name: string;
      };
      street: {
        mapbox_id: string;
        name: string;
      };
      postcode: {
        mapbox_id: string;
        name: string;
      };
      place: {
        mapbox_id: string;
        name: string;
        wikidata_id: string;
        alternate: {
          mapbox_id: string;
          name: string;
        };
      };
      district: {
        mapbox_id: string;
        name: string;
        wikidata_id: string;
      };
      region: {
        mapbox_id: string;
        name: string;
        wikidata_id: string;
        region_code: string;
        region_code_full: string;
      };
      country: {
        mapbox_id: string;
        name: string;
        wikidata_id: string;
        country_code: string;
        country_code_alpha_3: string;
      };
    };
  };
};
